'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, UserPlus, Building2, MapPin, Phone } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';

interface SignUpFormData {
  fullName: string;
  username: string;
  email: string;
  phone: string;
  whatsapp: string;
  password: string;
  confirmar: string;
  // Optional business info
  businessName: string;
  address: string;
  city: string;
  country: string;
  terminos: boolean;
}

interface SignUpFormProps {
  onSwitchToLogin: () => void;
}

export default function SignUpForm({ onSwitchToLogin }: SignUpFormProps) {
  const { signUp } = useAuth();
  const router = useRouter();
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showOptional, setShowOptional] = useState(false);

  const { register, handleSubmit, watch, formState: { errors } } = useForm<SignUpFormData>({
    defaultValues: { country: 'Honduras' },
  });
  const pwdValue = watch('password');

  const onSubmit = async (data: SignUpFormData) => {
    setLoading(true);
    try {
      await signUp(data.email, data.password, {
        fullName: data.fullName,
        username: data.username || undefined,
        phone: data.phone,
        whatsapp: data.whatsapp,
        businessName: data.businessName,
        address: data.address,
        city: data.city,
        country: data.country || 'Honduras',
        role: 'admin',
      });
      toast.success('Cuenta creada correctamente. Bienvenido a PrestaMass.');
      router.push('/');
      router.refresh();
    } catch (error: any) {
      const msg = error?.message || '';
      if (msg.includes('already registered') || msg.includes('already been registered')) {
        toast.error('Este correo electrónico ya está registrado.');
      } else if (msg.includes('Password should be')) {
        toast.error('La contraseña debe tener al menos 6 caracteres.');
      } else if (msg.includes('invalid email')) {
        toast.error('El correo electrónico no es válido.');
      } else {
        toast.error('Error al crear la cuenta. Intenta nuevamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-800 text-foreground">Crear cuenta</h1>
        <p className="text-sm text-muted-foreground mt-1">Nuevo usuario de PrestaMass</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Nombre completo */}
        <div>
          <label className="block text-sm font-600 text-foreground mb-1.5">Nombre completo <span className="text-danger">*</span></label>
          <input
            type="text"
            placeholder="Ej: María Elena Rodríguez"
            {...register('fullName', { required: 'El nombre es obligatorio', minLength: { value: 3, message: 'Mínimo 3 caracteres' } })}
            className={`w-full px-4 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all placeholder:text-muted-foreground ${errors.fullName ? 'border-danger bg-danger-bg' : 'border-border focus:border-primary/60'}`}
          />
          {errors.fullName && <p className="text-danger text-xs mt-1.5">{errors.fullName.message}</p>}
        </div>

        {/* Username */}
        <div>
          <label className="block text-sm font-600 text-foreground mb-1.5">Nombre de usuario <span className="text-muted-foreground text-xs">(opcional)</span></label>
          <input
            type="text"
            placeholder="Ej: maria.rodriguez"
            {...register('username')}
            className="w-full px-4 py-2.5 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all placeholder:text-muted-foreground focus:border-primary/60"
          />
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-600 text-foreground mb-1.5">Correo electrónico <span className="text-danger">*</span></label>
          <input
            type="email"
            placeholder="correo@ejemplo.com"
            {...register('email', {
              required: 'El correo es obligatorio',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Correo inválido' },
            })}
            className={`w-full px-4 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all placeholder:text-muted-foreground ${errors.email ? 'border-danger bg-danger-bg' : 'border-border focus:border-primary/60'}`}
          />
          {errors.email && <p className="text-danger text-xs mt-1.5">{errors.email.message}</p>}
        </div>

        {/* Phone & WhatsApp */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-600 text-foreground mb-1.5">
              <Phone size={12} className="inline mr-1" />Teléfono
            </label>
            <input
              type="tel"
              placeholder="+504 9999-9999"
              {...register('phone')}
              className="w-full px-4 py-2.5 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all placeholder:text-muted-foreground focus:border-primary/60"
            />
          </div>
          <div>
            <label className="block text-sm font-600 text-foreground mb-1.5">WhatsApp</label>
            <input
              type="tel"
              placeholder="+504 9999-9999"
              {...register('whatsapp')}
              className="w-full px-4 py-2.5 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all placeholder:text-muted-foreground focus:border-primary/60"
            />
          </div>
        </div>

        {/* Password */}
        <div>
          <label className="block text-sm font-600 text-foreground mb-1.5">Contraseña <span className="text-danger">*</span></label>
          <p className="text-xs text-muted-foreground mb-1.5">Mínimo 8 caracteres, incluye mayúscula y número</p>
          <div className="relative">
            <input
              type={showPwd ? 'text' : 'password'}
              placeholder="••••••••••"
              {...register('password', {
                required: 'La contraseña es obligatoria',
                minLength: { value: 8, message: 'Mínimo 8 caracteres' },
                pattern: { value: /(?=.*[A-Z])(?=.*\d)/, message: 'Debe incluir mayúscula y número' },
              })}
              className={`w-full px-4 py-2.5 pr-11 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all placeholder:text-muted-foreground ${errors.password ? 'border-danger bg-danger-bg' : 'border-border focus:border-primary/60'}`}
            />
            <button type="button" onClick={() => setShowPwd(p => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && <p className="text-danger text-xs mt-1.5">{errors.password.message}</p>}
        </div>

        {/* Confirm password */}
        <div>
          <label className="block text-sm font-600 text-foreground mb-1.5">Confirmar contraseña <span className="text-danger">*</span></label>
          <div className="relative">
            <input
              type={showConfirm ? 'text' : 'password'}
              placeholder="••••••••••"
              {...register('confirmar', {
                required: 'Confirma tu contraseña',
                validate: v => v === pwdValue || 'Las contraseñas no coinciden',
              })}
              className={`w-full px-4 py-2.5 pr-11 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all placeholder:text-muted-foreground ${errors.confirmar ? 'border-danger bg-danger-bg' : 'border-border focus:border-primary/60'}`}
            />
            <button type="button" onClick={() => setShowConfirm(p => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.confirmar && <p className="text-danger text-xs mt-1.5">{errors.confirmar.message}</p>}
        </div>

        {/* Optional business info toggle */}
        <button
          type="button"
          onClick={() => setShowOptional(p => !p)}
          className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 font-500 transition-colors"
        >
          <Building2 size={14} />
          {showOptional ? 'Ocultar' : 'Agregar'} información del negocio (opcional)
        </button>

        {showOptional && (
          <div className="space-y-3 p-4 bg-muted/30 rounded-xl border border-border">
            <div>
              <label className="block text-sm font-600 text-foreground mb-1.5">
                <Building2 size={12} className="inline mr-1" />Nombre del negocio
              </label>
              <input
                type="text"
                placeholder="Ej: Préstamos Rápidos HN"
                {...register('businessName')}
                className="w-full px-4 py-2.5 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all placeholder:text-muted-foreground focus:border-primary/60"
              />
            </div>
            <div>
              <label className="block text-sm font-600 text-foreground mb-1.5">
                <MapPin size={12} className="inline mr-1" />Dirección
              </label>
              <input
                type="text"
                placeholder="Dirección del negocio"
                {...register('address')}
                className="w-full px-4 py-2.5 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all placeholder:text-muted-foreground focus:border-primary/60"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-600 text-foreground mb-1.5">Ciudad</label>
                <input
                  type="text"
                  placeholder="Tegucigalpa"
                  {...register('city')}
                  className="w-full px-4 py-2.5 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all placeholder:text-muted-foreground focus:border-primary/60"
                />
              </div>
              <div>
                <label className="block text-sm font-600 text-foreground mb-1.5">País</label>
                <input
                  type="text"
                  placeholder="Honduras"
                  {...register('country')}
                  className="w-full px-4 py-2.5 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all placeholder:text-muted-foreground focus:border-primary/60"
                />
              </div>
            </div>
          </div>
        )}

        {/* Terms */}
        <label className="flex items-start gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            {...register('terminos', { required: 'Debes aceptar los términos' })}
            className="w-4 h-4 mt-0.5 rounded border-border text-primary focus:ring-primary/30 cursor-pointer shrink-0"
          />
          <span className="text-xs text-muted-foreground leading-relaxed">
            Acepto los{' '}
            <span className="text-primary font-500">Términos de Servicio</span>
            {' '}y la{' '}
            <span className="text-primary font-500">Política de Privacidad</span>
            {' '}de PrestaMass
          </span>
        </label>
        {errors.terminos && <p className="text-danger text-xs -mt-2">{errors.terminos.message}</p>}

        {/* Submit */}
        <button
          type="submit"
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 py-3 bg-primary text-white font-600 rounded-xl hover:bg-primary/90 transition-all active:scale-[0.98] disabled:opacity-60 text-sm"
        >
          {loading ? (
            <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Creando cuenta...</>
          ) : (
            <><UserPlus size={16} />Crear cuenta</>
          )}
        </button>
      </form>
    </div>
  );
}